import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import { 
  Sun, 
  Moon, 
  Calendar, 
  Sparkles, 
  AlertTriangle, 
  RefreshCw,
  Package,
  ArrowRight,
  Lightbulb,
  Clock,
  ChevronRight,
  Settings2,
  PackageX,
  Zap,
  CalendarDays,
  CheckCircle2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import WeeklySchedule from "@/components/WeeklySchedule";

// Days of the week
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_FULL_NAMES = {
  Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday",
  Fri: "Friday", Sat: "Saturday", Sun: "Sunday"
};

// Get today's day abbreviation
const getTodayAbbrev = () => {
  const dayIndex = new Date().getDay();
  // JavaScript: 0=Sun, 1=Mon, etc. Convert to our format
  return dayIndex === 0 ? "Sun" : DAYS[dayIndex - 1];
};

export default function Routines() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [products, setProducts] = useState([]);
  const [needsRegeneration, setNeedsRegeneration] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [routineSettings, setRoutineSettings] = useState({
    maxAmProducts: "5",
    maxPmProducts: "5"
  });
  
  // Weekly schedule state
  const [selectedDay, setSelectedDay] = useState(getTodayAbbrev());
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSchedule, setProductSchedule] = useState({ am: [], pm: [] });

  useEffect(() => {
    fetchRoutineAndProducts();
  }, []);

  const fetchRoutineAndProducts = async () => {
    try {
      const [routineRes, productsRes] = await Promise.all([
        axios.get(`${API}/routine`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/products`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setRoutine(routineRes.data);
      setProducts(productsRes.data);
      setNeedsRegeneration(routineRes.data?.needs_regeneration || false);
    } catch (error) {
      console.error("Failed to fetch routine:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateRoutine = async (force = false) => {
    if (products.length === 0) {
      toast.error("Add some products first to generate a routine");
      navigate("/add-product");
      return;
    }

    if (!user?.skin_type) {
      toast.error("Please set your skin type in Settings first");
      navigate("/settings");
      return;
    }

    if (!force && !needsRegeneration && routine?.am_routine?.length > 0) {
      toast.info("Your routine is up to date. Add or remove products to regenerate.");
      return;
    }

    setGenerating(true);
    try {
      const params = new URLSearchParams({
        force: force.toString(),
        max_am: routineSettings.maxAmProducts,
        max_pm: routineSettings.maxPmProducts
      });
      
      const response = await axios.post(`${API}/routine/generate?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      
      if (response.data.regenerated === false) {
        toast.info(response.data.message || "Routine is already up to date");
      } else {
        toast.success("Routine generated successfully!");
      }
      
      setRoutine(response.data);
      setNeedsRegeneration(false);
      setShowSettings(false);
    } catch (error) {
      console.error("Failed to generate routine:", error);
      toast.error(error.response?.data?.detail || "Failed to generate routine. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const toggleShelve = async (productId, currentShelved) => {
    try {
      const newShelved = !currentShelved;
      await axios.put(`${API}/products/${productId}/shelve?shelved=${newShelved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProducts(products.map(p => 
        p.id === productId ? { ...p, shelved: newShelved } : p
      ));
      
      toast.success(newShelved ? "Product shelved" : "Product unshelved - will be included in next routine");
      
      if (!newShelved) {
        setNeedsRegeneration(true);
      }
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  // Toggle pin status for AM/PM routine
  const togglePin = async (productId, timeOfDay, currentPinned, e) => {
    e.stopPropagation();
    try {
      const newPinned = !currentPinned;
      const response = await axios.put(
        `${API}/products/${productId}/pin?time_of_day=${timeOfDay}&pinned=${newPinned}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProducts(products.map(p => 
        p.id === productId 
          ? { ...p, is_pinned_am: response.data.is_pinned_am, is_pinned_pm: response.data.is_pinned_pm }
          : p
      ));
      
      toast.success(
        newPinned 
          ? `Pinned to ${timeOfDay.toUpperCase()} routine` 
          : `Unpinned from ${timeOfDay.toUpperCase()} routine`
      );
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  // Save product schedule
  const saveSchedule = async () => {
    if (!editingProduct) return;
    
    try {
      await axios.put(`${API}/products/${editingProduct.id}/schedule`, productSchedule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setProducts(products.map(p => 
        p.id === editingProduct.id ? { ...p, schedule: productSchedule } : p
      ));
      
      toast.success("Schedule saved! This product will now follow your weekly plan.");
      setShowScheduleDialog(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error("Failed to save schedule");
    }
  };

  // Open schedule dialog for a product
  const openScheduleDialog = (product) => {
    setEditingProduct(product);
    setProductSchedule(product.schedule || { am: [], pm: [] });
    setShowScheduleDialog(true);
  };

  // Toggle a day in the schedule
  const toggleDay = (timeOfDay, day) => {
    setProductSchedule(prev => {
      const current = prev[timeOfDay] || [];
      if (current.includes(day)) {
        return { ...prev, [timeOfDay]: current.filter(d => d !== day) };
      } else {
        return { ...prev, [timeOfDay]: [...current, day] };
      }
    });
  };

  // Get active products (retinol, AHA, BHA, etc.)
  const activeProducts = products.filter(p => p.is_active && !p.shelved);
  
  // Get shelved products
  const shelvedProducts = products.filter(p => p.shelved);

  // Filter routine steps by selected day (for weekly view)
  const filterByDay = (steps, timeOfDay) => {
    if (!steps) return [];
    
    return steps.filter(step => {
      const product = products.find(p => p.id === step.product_id);
      if (!product) return true; // Show if no product found
      
      // If product has no schedule, show every day
      if (!product.schedule || !product.schedule[timeOfDay] || product.schedule[timeOfDay].length === 0) {
        return true;
      }
      
      // Check if today is in the schedule
      return product.schedule[timeOfDay].includes(selectedDay);
    });
  };

  const RoutineStepCard = ({ step, index, showScheduleBtn = false }) => {
    const handleClick = () => {
      if (step.product_id) {
        navigate(`/products?highlight=${step.product_id}`);
      }
    };
    
    const product = products.find(p => p.id === step.product_id);
    const brand = step.brand || product?.brand;
    const isShelved = product?.shelved;
    const skiniqScore = product?.skiniq_score;
    const isBanned = product?.is_banned ?? false;
    const hasScore = skiniqScore !== null && skiniqScore !== undefined;
    const isActive = product?.is_active;
    const activeType = product?.active_type;
    const hasSchedule = product?.schedule && (product.schedule.am?.length > 0 || product.schedule.pm?.length > 0);
    const isPinnedAm = product?.is_pinned_am ?? false;
    const isPinnedPm = product?.is_pinned_pm ?? false;
    
    return (
      <div 
        className={`flex items-start gap-4 p-4 rounded-xl bg-white/50 border border-border/50 hover:border-primary/30 hover:bg-white/80 transition-all group ${isShelved ? 'opacity-50' : ''}`}
        data-testid={`routine-step-${index}`}
      >
        <div 
          className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium cursor-pointer"
          onClick={handleClick}
        >
          {index + 1}
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={handleClick}>
          {/* Product Name */}
          <h4 className="font-medium text-primary">{step.product_name}</h4>
          
          {/* Brand + Score + Badges on same line */}
          <div className="flex items-center gap-2 flex-wrap mt-1">
            {brand && (
              <p className="text-xs font-semibold text-primary/80 uppercase tracking-wide">{brand}</p>
            )}
            {/* SkinIQ Score Badge */}
            {!isShelved && hasScore && (
              <span className={`px-1.5 py-0.5 rounded text-xs font-bold text-white ${
                skiniqScore >= 85 ? 'bg-emerald-500' : 
                skiniqScore >= 65 ? 'bg-amber-500' : 'bg-rose-500'
              }`}>
                {skiniqScore}
              </span>
            )}
            {!isShelved && !hasScore && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 bg-gray-100">
                N/A
              </span>
            )}
            {/* Active Badge */}
            {isActive && !isShelved && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-300 text-purple-600 bg-purple-50">
                <Zap className="w-2.5 h-2.5 mr-0.5" />
                {activeType === 'retinol' ? 'Retinol' : 
                 activeType === 'aha' ? 'AHA' : 
                 activeType === 'bha' ? 'BHA' : 
                 activeType === 'vitamin_c' ? 'Vit C' : 'Active'}
              </Badge>
            )}
            {/* Schedule Badge */}
            {hasSchedule && !isShelved && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-teal-300 text-teal-600 bg-teal-50">
                <CalendarDays className="w-2.5 h-2.5 mr-0.5" />
                Scheduled
              </Badge>
            )}
            {isBanned && !isShelved && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                EU Banned
              </Badge>
            )}
            {isShelved && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gray-200 text-gray-600">
                Shelved
              </Badge>
            )}
          </div>
          
          <p className="text-sm text-muted-foreground mt-1">{step.instruction}</p>
          {step.reason && (
            <p className="text-xs text-primary/70 mt-2 italic">{step.reason}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Pin Icons */}
          {!isShelved && product && (
            <>
              <button
                onClick={(e) => togglePin(product.id, 'am', isPinnedAm, e)}
                className={`p-1 rounded transition-all ${
                  isPinnedAm 
                    ? 'text-amber-500 bg-amber-100' 
                    : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'
                }`}
                title={isPinnedAm ? "Unpin from AM" : "Pin to AM"}
              >
                <Sun className={`w-4 h-4 ${isPinnedAm ? 'fill-amber-200' : ''}`} />
              </button>
              <button
                onClick={(e) => togglePin(product.id, 'pm', isPinnedPm, e)}
                className={`p-1 rounded transition-all ${
                  isPinnedPm 
                    ? 'text-indigo-500 bg-indigo-100' 
                    : 'text-gray-300 hover:text-indigo-400 hover:bg-indigo-50'
                }`}
                title={isPinnedPm ? "Unpin from PM" : "Pin to PM"}
              >
                <Moon className={`w-4 h-4 ${isPinnedPm ? 'fill-indigo-200' : ''}`} />
              </button>
            </>
          )}
          {/* Schedule Button for Actives */}
          {showScheduleBtn && isActive && !isShelved && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
              onClick={(e) => {
                e.stopPropagation();
                if (product) openScheduleDialog(product);
              }}
              title="Set weekly schedule"
            >
              <CalendarDays className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${isShelved ? 'text-primary' : 'text-muted-foreground hover:text-amber-600'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (product) toggleShelve(product.id, product.shelved);
            }}
            title={isShelved ? "Unshelve product" : "Shelve product (out of stock)"}
          >
            <PackageX className="w-4 h-4" />
          </Button>
          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 group-hover:text-primary group-hover:translate-x-1 transition-all cursor-pointer" onClick={handleClick} />
        </div>
      </div>
    );
  };

  const RoutineSection = ({ title, icon: Icon, steps, emptyText, variant, showScheduleBtn = false }) => (
    <Card className={`glass overflow-hidden ${variant === 'am' ? 'border-amber-200/50' : 'border-indigo-200/50'}`}>
      <CardHeader className={`${variant === 'am' ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-gradient-to-r from-indigo-50 to-purple-50'}`}>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className={`p-2 rounded-lg ${variant === 'am' ? 'bg-amber-100' : 'bg-indigo-100'}`}>
            <Icon className={`w-5 h-5 ${variant === 'am' ? 'text-amber-600' : 'text-indigo-600'}`} />
          </div>
          {title}
          {steps?.length > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {steps.length} steps
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {steps && steps.length > 0 ? (
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <RoutineStepCard key={idx} step={step} index={idx} showScheduleBtn={showScheduleBtn} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{emptyText}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Day Selector Component for Weekly View
  const DaySelector = () => (
    <div className="flex justify-center gap-2 mb-6" data-testid="day-selector">
      {DAYS.map(day => {
        const isToday = day === getTodayAbbrev();
        const isSelected = day === selectedDay;
        
        return (
          <button
            key={day}
            onClick={() => setSelectedDay(day)}
            className={`
              w-12 h-12 rounded-xl font-semibold text-sm transition-all
              ${isSelected 
                ? 'bg-primary text-white shadow-lg scale-105' 
                : isToday 
                  ? 'bg-primary/10 text-primary border-2 border-primary/30'
                  : 'bg-white/50 text-gray-600 hover:bg-white hover:shadow-md'
              }
            `}
            data-testid={`day-${day}`}
          >
            {day}
          </button>
        );
      })}
    </div>
  );

  // Active Products Section for Weekly Schedule Management
  const ActiveProductsManager = () => {
    if (activeProducts.length === 0) {
      return (
        <Card className="glass border-purple-200/50 mb-6">
          <CardContent className="text-center py-8">
            <Zap className="w-12 h-12 mx-auto text-purple-300 mb-3" />
            <h3 className="font-medium text-primary mb-2">No Active Products Detected</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Products containing retinol, AHA, BHA, or Vitamin C will appear here for weekly scheduling.
              This helps prevent over-exfoliation by alternating strong actives.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="glass border-purple-200/50 mb-6">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="p-2 rounded-lg bg-purple-100">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            Active Products Schedule
            <Badge variant="secondary" className="ml-auto bg-purple-100 text-purple-700">
              {activeProducts.length} actives
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground mb-4">
            Set a weekly schedule for your active ingredients to prevent over-exfoliation. 
            Products without a schedule will appear every day.
          </p>
          <div className="space-y-3">
            {activeProducts.map(product => {
              const hasSchedule = product.schedule && (product.schedule.am?.length > 0 || product.schedule.pm?.length > 0);
              
              return (
                <div 
                  key={product.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/50 border border-border/50"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="border-purple-300 text-purple-600 bg-purple-50">
                      {product.active_type === 'retinol' ? 'Retinol' : 
                       product.active_type === 'aha' ? 'AHA' : 
                       product.active_type === 'bha' ? 'BHA' : 
                       product.active_type === 'vitamin_c' ? 'Vit C' : 'Active'}
                    </Badge>
                    <div>
                      <p className="font-medium text-primary text-sm">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.brand}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasSchedule ? (
                      <div className="flex gap-1 mr-2">
                        {DAYS.map(day => {
                          const inAm = product.schedule.am?.includes(day);
                          const inPm = product.schedule.pm?.includes(day);
                          return (
                            <div 
                              key={day} 
                              className={`w-6 h-6 rounded text-[10px] font-medium flex items-center justify-center ${
                                inAm || inPm ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'
                              }`}
                              title={`${day}: ${inAm ? 'AM' : ''} ${inPm ? 'PM' : ''}`}
                            >
                              {day[0]}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground mr-2">Every day</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openScheduleDialog(product)}
                      className="border-purple-200 text-purple-600 hover:bg-purple-50"
                    >
                      <CalendarDays className="w-4 h-4 mr-1" />
                      {hasSchedule ? 'Edit' : 'Set Schedule'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-pulse-soft text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <div className="w-8 h-8 rounded-full bg-primary/40"></div>
            </div>
            <p className="mt-4 text-muted-foreground">Loading routine...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasRoutine = routine && (routine.am_routine?.length > 0 || routine.pm_routine?.length > 0);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="routines-page">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <h1 className="font-serif text-3xl text-primary mb-2">My Routine</h1>
            <p className="text-muted-foreground">
              {hasRoutine 
                ? `Personalized for ${user?.skin_type || 'your'} skin`
                : "Generate your personalized skincare routine"
              }
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate("/products")}
              className="border-primary/20"
              data-testid="view-products-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Products ({products.length})
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowSettings(!showSettings)}
              className="border-primary/20"
              data-testid="routine-settings-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              onClick={() => generateRoutine(false)}
              disabled={generating || products.length === 0}
              className={needsRegeneration ? "btn-primary" : "btn-outline"}
              data-testid="generate-routine-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : needsRegeneration ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Update Routine
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {hasRoutine ? "Routine Up to Date" : "Generate Routine"}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Routine Settings Panel */}
        {showSettings && (
          <Card className="glass mb-6 animate-fade-in-up">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings2 className="w-5 h-5" />
                Routine Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="max-am" className="flex items-center gap-2">
                    <Sun className="w-4 h-4 text-amber-500" />
                    Max Morning Products
                  </Label>
                  <Select 
                    value={routineSettings.maxAmProducts} 
                    onValueChange={(v) => setRoutineSettings({...routineSettings, maxAmProducts: v})}
                  >
                    <SelectTrigger id="max-am" data-testid="max-am-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 products (minimal)</SelectItem>
                      <SelectItem value="4">4 products</SelectItem>
                      <SelectItem value="5">5 products (recommended)</SelectItem>
                      <SelectItem value="6">6 products</SelectItem>
                      <SelectItem value="7">7 products (full routine)</SelectItem>
                      <SelectItem value="10">All products</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-pm" className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-indigo-500" />
                    Max Evening Products
                  </Label>
                  <Select 
                    value={routineSettings.maxPmProducts} 
                    onValueChange={(v) => setRoutineSettings({...routineSettings, maxPmProducts: v})}
                  >
                    <SelectTrigger id="max-pm" data-testid="max-pm-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 products (minimal)</SelectItem>
                      <SelectItem value="4">4 products</SelectItem>
                      <SelectItem value="5">5 products (recommended)</SelectItem>
                      <SelectItem value="6">6 products</SelectItem>
                      <SelectItem value="7">7 products (full routine)</SelectItem>
                      <SelectItem value="10">All products</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowSettings(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => generateRoutine(true)} 
                  disabled={generating}
                  className="btn-primary"
                  data-testid="apply-settings-btn"
                >
                  {generating ? "Generating..." : "Apply & Regenerate"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Changed Notice */}
        {needsRegeneration && hasRoutine && (
          <Card className="glass border-amber-200 bg-amber-50/50 mb-6 animate-fade-in-up">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Products have changed</p>
                <p className="text-sm text-amber-700">You've added or removed products since your last routine was generated.</p>
              </div>
              <Button 
                onClick={() => generateRoutine(false)}
                disabled={generating}
                className="btn-primary"
                data-testid="update-routine-btn"
              >
                {generating ? "Updating..." : "Update Routine"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Products State */}
        {products.length === 0 && (
          <Card className="glass text-center py-16 animate-fade-in-up">
            <CardContent>
              <Package className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-serif text-xl text-primary mb-2">No Products Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Add your skincare products first, then we'll create a personalized routine based on your skin type and product ingredients.
              </p>
              <Button 
                onClick={() => navigate("/add-product")}
                className="btn-primary"
                data-testid="add-first-product-btn"
              >
                Add Your First Product
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Skin Type Warning */}
        {products.length > 0 && !user?.skin_type && (
          <Card className="glass border-amber-200 bg-amber-50/50 mb-6 animate-fade-in-up">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Set your skin type</p>
                <p className="text-sm text-amber-700">For a more personalized routine, set your skin type in settings.</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate("/settings")}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                data-testid="set-skin-type-btn"
              >
                Go to Settings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Routine Content */}
        {products.length > 0 && (
          <Tabs defaultValue="daily" className="animate-fade-in-up stagger-1">
            <TabsList className="glass mb-6">
              <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="daily-tab">
                <Clock className="w-4 h-4" />
                Daily Routine
              </TabsTrigger>
              <TabsTrigger value="weekly" className="flex items-center gap-2" data-testid="weekly-tab">
                <Calendar className="w-4 h-4" />
                Weekly Schedule
              </TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="space-y-6">
              {!hasRoutine ? (
                <Card className="glass text-center py-16">
                  <CardContent>
                    <Sparkles className="w-16 h-16 mx-auto text-primary/30 mb-4" />
                    <h3 className="font-serif text-xl text-primary mb-2">No Routine Yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Click "Generate Routine" to create your personalized AM/PM skincare routine based on your {products.length} products.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid lg:grid-cols-2 gap-6">
                  <RoutineSection 
                    title="Morning Routine" 
                    icon={Sun}
                    steps={routine.am_routine}
                    emptyText="No morning products in your routine"
                    variant="am"
                  />
                  <RoutineSection 
                    title="Evening Routine" 
                    icon={Moon}
                    steps={routine.pm_routine}
                    emptyText="No evening products in your routine"
                    variant="pm"
                  />
                </div>
              )}

              {/* Tips & Warnings */}
              {hasRoutine && (routine.tips?.length > 0 || routine.warnings?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-6">
                  {routine.tips?.length > 0 && (
                    <Card className="glass border-green-200/50">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                        <CardTitle className="flex items-center gap-3 text-lg">
                          <div className="p-2 rounded-lg bg-green-100">
                            <Lightbulb className="w-5 h-5 text-green-600" />
                          </div>
                          Tips
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {routine.tips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="text-green-500 mt-1">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {routine.warnings?.length > 0 && (
                    <Card className="glass border-amber-200/50">
                      <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                        <CardTitle className="flex items-center gap-3 text-lg">
                          <div className="p-2 rounded-lg bg-amber-100">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          </div>
                          Warnings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {routine.warnings.map((warning, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="text-amber-500 mt-1">•</span>
                              {warning}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="weekly" className="space-y-6">
              {!hasRoutine ? (
                <Card className="glass text-center py-16">
                  <CardContent>
                    <Calendar className="w-16 h-16 mx-auto text-primary/30 mb-4" />
                    <h3 className="font-serif text-xl text-primary mb-2">No Weekly Schedule</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Generate a routine first to see your weekly schedule with product rotation recommendations.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Day Selector */}
                  <Card className="glass">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <CalendarDays className="w-5 h-5 text-primary" />
                        Weekly View - {DAY_FULL_NAMES[selectedDay]}
                        {selectedDay === getTodayAbbrev() && (
                          <Badge className="ml-2 bg-primary/10 text-primary">Today</Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <DaySelector />
                      <p className="text-sm text-muted-foreground text-center mb-4">
                        Select a day to see which products are scheduled. Products with actives can be set to specific days.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Filtered Routine for Selected Day */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    <RoutineSection 
                      title={`${selectedDay} Morning`}
                      icon={Sun}
                      steps={filterByDay(routine.am_routine, 'am')}
                      emptyText="No morning products scheduled for this day"
                      variant="am"
                      showScheduleBtn={true}
                    />
                    <RoutineSection 
                      title={`${selectedDay} Evening`}
                      icon={Moon}
                      steps={filterByDay(routine.pm_routine, 'pm')}
                      emptyText="No evening products scheduled for this day"
                      variant="pm"
                      showScheduleBtn={true}
                    />
                  </div>

                  {/* Active Products Manager */}
                  <ActiveProductsManager />
                  
                  {/* Weekly Schedule Grid for Active Types */}
                  <WeeklySchedule activeProducts={activeProducts} />
                </>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Shelved Products Section */}
        {shelvedProducts.length > 0 && (
          <Card className="glass border-gray-200/50 mt-6 animate-fade-in-up">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50">
              <CardTitle className="flex items-center gap-3 text-lg">
                <div className="p-2 rounded-lg bg-gray-100">
                  <PackageX className="w-5 h-5 text-gray-600" />
                </div>
                Shelved Products
                <Badge variant="secondary" className="ml-auto">
                  {shelvedProducts.length} items
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                These products are out of stock and excluded from routine generation. Click to bring them back.
              </p>
              <div className="flex flex-wrap gap-2">
                {shelvedProducts.map(product => (
                  <Button
                    key={product.id}
                    variant="outline"
                    size="sm"
                    className="bg-gray-50 hover:bg-white"
                    onClick={() => toggleShelve(product.id, true)}
                    data-testid={`unshelve-${product.id}`}
                  >
                    <PackageX className="w-3 h-3 mr-2 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">{product.brand}</span>
                    <span className="text-xs text-gray-500 ml-1 truncate max-w-[150px]">{product.name}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Last Updated */}
        {hasRoutine && routine.updated_at && (
          <p className="text-xs text-muted-foreground text-center mt-8 animate-fade-in-up stagger-2">
            Last updated: {new Date(routine.updated_at).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        )}
      </main>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              Set Weekly Schedule
            </DialogTitle>
            <DialogDescription>
              Choose which days this active product appears in your routine.
              This helps prevent over-exfoliation and skin irritation.
            </DialogDescription>
          </DialogHeader>
          
          {editingProduct && (
            <div className="space-y-6">
              {/* Product Info */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="border-purple-300 text-purple-600">
                    {editingProduct.active_type === 'retinol' ? 'Retinol' : 
                     editingProduct.active_type === 'aha' ? 'AHA' : 
                     editingProduct.active_type === 'bha' ? 'BHA' : 
                     editingProduct.active_type === 'vitamin_c' ? 'Vitamin C' : 'Active'}
                  </Badge>
                </div>
                <p className="font-medium text-primary">{editingProduct.name}</p>
                <p className="text-sm text-muted-foreground">{editingProduct.brand}</p>
              </div>

              {/* AM Schedule */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <Label className="text-sm font-medium">Morning (AM)</Label>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(day => (
                    <button
                      key={`am-${day}`}
                      onClick={() => toggleDay('am', day)}
                      className={`w-12 h-10 rounded-lg text-sm font-medium transition-all ${
                        productSchedule.am?.includes(day)
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* PM Schedule */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  <Label className="text-sm font-medium">Evening (PM)</Label>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(day => (
                    <button
                      key={`pm-${day}`}
                      onClick={() => toggleDay('pm', day)}
                      className={`w-12 h-10 rounded-lg text-sm font-medium transition-all ${
                        productSchedule.pm?.includes(day)
                          ? 'bg-indigo-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Presets */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Quick Presets</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductSchedule({ am: [], pm: ['Mon', 'Wed', 'Fri'] })}
                  >
                    PM: Mon/Wed/Fri
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductSchedule({ am: [], pm: ['Tue', 'Thu', 'Sat'] })}
                  >
                    PM: Tue/Thu/Sat
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductSchedule({ am: ['Mon', 'Wed', 'Fri'], pm: [] })}
                  >
                    AM: Mon/Wed/Fri
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductSchedule({ am: [], pm: [] })}
                  >
                    Every Day
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={saveSchedule} className="bg-purple-600 hover:bg-purple-700">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save Schedule
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
